import "react-advanced-cropper/dist/style.css"

import { yupResolver } from "@hookform/resolvers/yup"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { Camera } from "iconsax-reactjs"
import { X } from "lucide-react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Cropper, CropperRef } from "react-advanced-cropper"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import * as yup from "yup"

import { uploadBack, uploadFront } from "@/api"
import { BottomSheetModal, MessageError, Select } from "@/components/shared"
import { Button, DatePicker, Input, Label } from "@/components/ui"
import { useIdentifyCard } from "@/modules/helpers/hooks"
import { getImageByNameFormatV2 } from "@/modules/helpers/utils"

// ==================== Types ====================
export interface IdentifyCardInfo {
  identifyNumber?: string
  fullName?: string
  dateGrantIdentify?: string
  dateExpireIdentify?: string
  gender?: string
  nationality?: string
  frontImage?: File
  backImage?: File
  identifyNumberFrontImage?: string
  identifyNumberBackImage?: string
  placeGrantIdentify?: string
  placeOfOrigin?: string
  placeOfResidence?: string
}

interface IdentityCardSheetProps {
  isVisible: boolean
  setIsVisible: (status: boolean) => void
  currentData?: IdentifyCardInfo
}

type ImageSide = "front" | "back"

// ==================== Constants ====================
const PLACE_GRANT_OPTIONS = [
  {
    value: "Cục Cảnh sát Quản lý hành chính về trật tự xã hội",
    label: "Cục Cảnh sát Quản lý hành chính về trật tự xã hội"
  },
  {
    value: "Phòng Cảnh sát Quản lý hành chính về trật tự xã hội",
    label: "Phòng Cảnh sát Quản lý hành chính về trật tự xã hội"
  }
]

const DEFAULT_FORM_VALUES: IdentifyCardInfo = {
  identifyNumber: "",
  fullName: "",
  dateGrantIdentify: "",
  dateExpireIdentify: "",
  gender: "",
  nationality: "",
  frontImage: undefined,
  backImage: undefined,
  identifyNumberFrontImage: "",
  identifyNumberBackImage: "",
  placeGrantIdentify: "",
  placeOfOrigin: "",
  placeOfResidence: ""
}

// ==================== Validation Schema ====================
const validationSchema = yup
  .object({
    identifyNumber: yup
      .string()
      .required("Số CCCD là bắt buộc")
      .matches(/^[0-9]{9,12}$/, "Số CCCD phải có từ 9-12 chữ số"),
    fullName: yup
      .string()
      .required("Họ và tên là bắt buộc")
      .min(2, "Họ và tên phải có ít nhất 2 ký tự")
      .max(100, "Họ và tên không được quá 100 ký tự"),
    dateGrantIdentify: yup.string().required("Ngày cấp là bắt buộc"),
    dateExpireIdentify: yup.string().optional(),
    gender: yup.string().optional(),
    nationality: yup.string().optional(),
    frontImage: yup.mixed<File>().optional(),
    backImage: yup.mixed<File>().optional(),
    identifyNumberFrontImage: yup.string().optional(),
    identifyNumberBackImage: yup.string().optional(),
    placeGrantIdentify: yup.string().required("Nơi cấp là bắt buộc"),
    placeOfOrigin: yup.string().required("Quê quán là bắt buộc"),
    placeOfResidence: yup.string().required("Nơi thường trú là bắt buộc")
  })
  .test("images-required", "Vui lòng chụp hoặc có ảnh mặt trước và mặt sau", function (value) {
    const hasFrontImage = value.frontImage || value.identifyNumberFrontImage
    const hasBackImage = value.backImage || value.identifyNumberBackImage

    if (!hasFrontImage) {
      return this.createError({ path: "frontImage", message: "Ảnh mặt trước là bắt buộc" })
    }
    if (!hasBackImage) {
      return this.createError({ path: "backImage", message: "Ảnh mặt sau là bắt buộc" })
    }
    return true
  })

// ==================== Helper Functions ====================
const formatDateGrantIdentify = (dateStr: string | undefined): string => {
  if (!dateStr) return ""

  // Already in DD/MM/YYYY format
  if (dateStr.includes("/")) return dateStr

  // Convert DD-MM-YYYY to DD/MM/YYYY
  if (dateStr.includes("-")) return dateStr.replace(/-/g, "/")

  // Try parsing with dayjs
  try {
    return dayjs(dateStr).format("DD/MM/YYYY")
  } catch {
    return dateStr
  }
}

const createBlobUrl = (file: File, urlRef: React.MutableRefObject<string>): string => {
  // Cleanup old URL
  if (urlRef.current?.startsWith("blob:")) {
    URL.revokeObjectURL(urlRef.current)
  }
  // Create new URL
  const url = URL.createObjectURL(file)
  urlRef.current = url
  return url
}

const cleanupBlobUrl = (urlRef: React.MutableRefObject<string>): void => {
  if (urlRef.current?.startsWith("blob:")) {
    URL.revokeObjectURL(urlRef.current)
    urlRef.current = ""
  }
}

const getFormValuesFromData = (data?: IdentifyCardInfo): IdentifyCardInfo => ({
  identifyNumber: data?.identifyNumber || "",
  fullName: data?.fullName || "",
  dateGrantIdentify: data?.dateGrantIdentify || "",
  dateExpireIdentify: data?.dateExpireIdentify || "",
  gender: data?.gender || "",
  nationality: data?.nationality || "",
  frontImage: data?.frontImage || undefined,
  backImage: data?.backImage || undefined,
  identifyNumberFrontImage: data?.identifyNumberFrontImage || "",
  identifyNumberBackImage: data?.identifyNumberBackImage || "",
  placeGrantIdentify: data?.placeGrantIdentify || "",
  placeOfOrigin: data?.placeOfOrigin || "",
  placeOfResidence: data?.placeOfResidence || ""
})

// ==================== Component ====================
export const IdentityCardSheet: React.FC<IdentityCardSheetProps> = ({
  isVisible,
  setIsVisible,
  currentData
}) => {
  const queryClient = useQueryClient()

  // Form setup
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: currentData || DEFAULT_FORM_VALUES,
    mode: "onChange"
  })

  // API mutation
  const { mutate, isPending } = useIdentifyCard({
    onSuccess: () => {
      setIsVisible(false)
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    },
    onError: (error: any) => {
      toast.error(error.message)
    },
    onSettled: () => {
      setIsVisible(false)
    }
  })

  // State
  const [isProcessing, setIsProcessing] = useState(false)
  const [showCrop, setShowCrop] = useState(false)
  const [capturedImage, setCapturedImage] = useState("")
  const [currentCropSide, setCurrentCropSide] = useState<ImageSide>("front")

  // Refs
  const cropperRef = useRef<CropperRef>(null)
  const frontImageUrlRef = useRef("")
  const backImageUrlRef = useRef("")
  const hasInitializedRef = useRef(false)
  const userHasInteractedRef = useRef(false)

  // Watch image fields
  const [frontImage, identifyNumberFrontImage, backImage, identifyNumberBackImage] = watch([
    "frontImage",
    "identifyNumberFrontImage",
    "backImage",
    "identifyNumberBackImage"
  ])

  // ==================== Memoized Values ====================
  const frontImageSrc = useMemo(() => {
    if (frontImage instanceof File) {
      return createBlobUrl(frontImage, frontImageUrlRef)
    }
    cleanupBlobUrl(frontImageUrlRef)
    return getImageByNameFormatV2(identifyNumberFrontImage) || ""
  }, [frontImage, identifyNumberFrontImage])

  const backImageSrc = useMemo(() => {
    if (backImage instanceof File) {
      return createBlobUrl(backImage, backImageUrlRef)
    }
    cleanupBlobUrl(backImageUrlRef)
    return getImageByNameFormatV2(identifyNumberBackImage) || ""
  }, [backImage, identifyNumberBackImage])

  // ==================== Callbacks ====================
  const loadImageFromUrl = useCallback(
    async (imageUrl: string, side: ImageSide) => {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const fileName = `${side}_image.jpg`
        const file = new File([blob], fileName, { type: blob.type || "image/jpeg" })

        setValue(side === "front" ? "frontImage" : "backImage", file, { shouldValidate: true })
        setValue(
          side === "front" ? "identifyNumberFrontImage" : "identifyNumberBackImage",
          imageUrl,
          { shouldValidate: true }
        )
      } catch (error) {
        console.error("Error loading image from URL:", error)
        setValue(
          side === "front" ? "identifyNumberFrontImage" : "identifyNumberBackImage",
          imageUrl,
          { shouldValidate: true }
        )
      }
    },
    [setValue]
  )

  const processCroppedImage = useCallback(
    async (blob: Blob, side: ImageSide) => {
      setIsProcessing(true)
      try {
        const fileName = `${side}_image.jpg`
        const file = new File([blob], fileName, { type: "image/jpeg" })

        if (side === "front") {
          const response = await uploadFront(file)
          if (response.data?.success && response.data.data) {
            const { identifyNumberFrontImage, identifyNumber, fullName } = response.data.data
            setValue("frontImage", file, { shouldValidate: true })
            if (identifyNumberFrontImage) {
              setValue("identifyNumberFrontImage", identifyNumberFrontImage, {
                shouldValidate: true
              })
            }
            if (identifyNumber) setValue("identifyNumber", identifyNumber, { shouldValidate: true })
            if (fullName) setValue("fullName", fullName, { shouldValidate: true })
            toast.success("Tải ảnh lên thành công")
          } else {
            toast.error("Không thể nhận diện thông tin mặt trước")
          }
        } else {
          const response = await uploadBack(file)
          if (response.data?.success && response.data.data) {
            const { identifyNumberBackImage, dateGrantIdentify } = response.data.data
            setValue("backImage", file, { shouldValidate: true })
            setValue("identifyNumberBackImage", identifyNumberBackImage || "", {
              shouldValidate: true
            })
            if (dateGrantIdentify) {
              setValue("dateGrantIdentify", formatDateGrantIdentify(dateGrantIdentify), {
                shouldValidate: true
              })
            }
            toast.success("Tải ảnh lên thành công")
          } else {
            toast.error("Không thể nhận diện thông tin mặt sau")
          }
        }
      } catch {
        toast.error("Lỗi khi xử lý ảnh")
      } finally {
        setIsProcessing(false)
      }
    },
    [setValue]
  )

  const cropImage = useCallback(() => {
    const canvas = cropperRef.current?.getCanvas()
    if (!canvas) return

    canvas.toBlob(
      async (blob) => {
        if (blob) {
          setShowCrop(false)
          setCapturedImage("")
          await processCroppedImage(blob, currentCropSide)
        }
      },
      "image/jpeg",
      0.9
    )
  }, [currentCropSide, processCroppedImage])

  const retakePhoto = useCallback(() => {
    setShowCrop(false)
    setCapturedImage("")
  }, [])

  const handleFileInput = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, side: ImageSide) => {
      const file = e.target.files?.[0]
      if (!file) return

      // Prevent form re-initialization after user interaction
      userHasInteractedRef.current = true
      e.target.value = ""
      setCurrentCropSide(side)

      try {
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })
        setCapturedImage(dataUrl)
        setShowCrop(true)
      } catch {
        toast.error("Lỗi khi xử lý ảnh")
      }
    },
    []
  )

  const handleSave = useCallback(
    (data: IdentifyCardInfo) => {
      const payload = {
        identifyNumber: data.identifyNumber,
        placeGrantIdentify: data.placeGrantIdentify,
        placeOfOrigin: data.placeOfOrigin,
        placeOfResidence: data.placeOfResidence,
        ...(!data.identifyNumberFrontImage?.includes("https://") && {
          identifyNumberFrontImage: data.identifyNumberFrontImage
        }),
        ...(!data.identifyNumberBackImage?.includes("https://") && {
          identifyNumberBackImage: data.identifyNumberBackImage
        }),
        fullName: data.fullName,
        dateGrantIdentify: data.dateGrantIdentify
      }
      mutate(payload)
    },
    [mutate]
  )

  // ==================== Effects ====================
  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      cleanupBlobUrl(frontImageUrlRef)
      cleanupBlobUrl(backImageUrlRef)
    }
  }, [])

  // Reset flags when modal closes
  useEffect(() => {
    if (!isVisible) {
      hasInitializedRef.current = false
      userHasInteractedRef.current = false
    }
  }, [isVisible])

  // Initialize form when modal opens
  useEffect(() => {
    if (!isVisible || userHasInteractedRef.current) return

    if (currentData && !hasInitializedRef.current) {
      reset(getFormValuesFromData(currentData))

      if (currentData.identifyNumberFrontImage && !currentData.frontImage) {
        loadImageFromUrl(currentData.identifyNumberFrontImage, "front")
      }
      if (currentData.identifyNumberBackImage && !currentData.backImage) {
        loadImageFromUrl(currentData.identifyNumberBackImage, "back")
      }
      hasInitializedRef.current = true
    } else if (!currentData && !hasInitializedRef.current) {
      reset(DEFAULT_FORM_VALUES)
    }
  }, [isVisible, currentData, reset, loadImageFromUrl])

  // ==================== Render ====================
  return (
    <BottomSheetModal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      title="Thông tin Căn cước công dân"
      description=""
    >
      <div className="h-[60vh] overflow-auto">
        <div className="p-4 space-y-4">
          {/* Upload Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Tải lên <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-3">
              {/* Front Image */}
              <label className="flex-1 space-y-2">
                {frontImage || identifyNumberFrontImage ? (
                  <div className="relative">
                    <img
                      src={frontImageSrc}
                      alt="Ảnh mặt trước"
                      className="w-full aspect-[1.6] object-cover rounded-lg border-2 border-gray-200"
                    />
                    <div className="absolute bottom-2 left-0 right-0 text-center">
                      <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                        Ảnh mặt trước
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg aspect-[1.6] flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Camera size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Ảnh mặt trước</span>
                  </div>
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileInput(e, "front")}
                />
                <div className="w-full text-white flex justify-center items-center rounded-full h-8 bg-primary hover:bg-primary/90 text-xs cursor-pointer">
                  Chụp lại
                </div>
              </label>

              {/* Back Image */}
              <label className="flex-1 space-y-2">
                {backImage || identifyNumberBackImage ? (
                  <div className="relative">
                    <img
                      src={backImageSrc}
                      alt="Ảnh mặt sau"
                      className="w-full aspect-[1.6] object-cover rounded-lg border-2 border-gray-200"
                    />
                    <div className="absolute bottom-2 left-0 right-0 text-center">
                      <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                        Ảnh mặt sau
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg aspect-[1.6] flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Camera size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Ảnh mặt sau</span>
                  </div>
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileInput(e, "back")}
                />
                <div className="w-full text-white flex justify-center items-center rounded-full h-8 bg-primary hover:bg-primary/90 text-xs cursor-pointer">
                  Chụp lại
                </div>
              </label>
            </div>
            {(errors.frontImage || errors.backImage) && (
              <p className="text-xs text-red-500">
                {errors.frontImage?.message || errors.backImage?.message}
              </p>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* ID Number */}
            <div className="space-y-2">
              <Label htmlFor="identifyNumber" className="text-sm">
                Số căn cước công dân <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="identifyNumber"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.identifyNumber?.message}>
                    <Input
                      id="identifyNumber"
                      {...field}
                      placeholder="Nhập số CCCD"
                      className="h-11"
                    />
                  </MessageError>
                )}
              />
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-sm">
                Họ và tên trên CCCD <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.fullName?.message}>
                    <Input id="fullName" {...field} placeholder="Nhập họ và tên" className="h-11" />
                  </MessageError>
                )}
              />
            </div>

            {/* Place of Origin */}
            <div className="space-y-2">
              <Label htmlFor="placeOfOrigin" className="text-sm">
                Quê quán <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="placeOfOrigin"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.placeOfOrigin?.message}>
                    <Input
                      id="placeOfOrigin"
                      {...field}
                      placeholder="Nhập quê quán"
                      className="h-11"
                    />
                  </MessageError>
                )}
              />
            </div>

            {/* Place of Residence */}
            <div className="space-y-2">
              <Label htmlFor="placeOfResidence" className="text-sm">
                Nơi thường trú <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="placeOfResidence"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.placeOfResidence?.message}>
                    <Input
                      id="placeOfResidence"
                      {...field}
                      placeholder="Nhập nơi thường trú"
                      className="h-11"
                    />
                  </MessageError>
                )}
              />
            </div>

            {/* Date Granted */}
            <div className="space-y-2">
              <Label className="text-sm">
                Ngày, tháng, năm cấp thẻ <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="dateGrantIdentify"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.dateGrantIdentify?.message}>
                    <DatePicker value={field.value} onChange={field.onChange} />
                  </MessageError>
                )}
              />
            </div>

            {/* Place Granted */}
            <div className="space-y-2">
              <Label htmlFor="placeGrantIdentify" className="text-sm">
                Nơi cấp <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="placeGrantIdentify"
                control={control}
                render={({ field }) => (
                  <MessageError error={errors.placeGrantIdentify?.message}>
                    <Select
                      options={PLACE_GRANT_OPTIONS}
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="Nhập nơi cấp"
                      className="!h-12 w-full rounded-xl"
                    />
                  </MessageError>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="p-4 border-t">
        <Button
          type="button"
          className="w-full !h-12 bg-gradient-primary text-white font-medium rounded-lg"
          onClick={handleSubmit(handleSave)}
          disabled={!isValid || isPending}
        >
          {isPending ? "Đang lưu..." : "Lưu"}
        </Button>
      </div>

      {/* Crop Modal */}
      {showCrop && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Cắt ảnh {currentCropSide === "front" ? "mặt trước" : "mặt sau"}
              </h3>
              <Button variant="ghost" size="sm" onClick={retakePhoto} className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-4">
              <div className="h-64 w-full">
                <Cropper
                  ref={cropperRef}
                  src={capturedImage}
                  className="cropper"
                  stencilProps={{ aspectRatio: 1.6 }}
                />
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t">
              <Button variant="outline" className="flex-1" onClick={retakePhoto}>
                Chụp lại
              </Button>
              <Button className="flex-1 bg-primary-800" onClick={cropImage} disabled={isProcessing}>
                {isProcessing ? "Đang xử lý..." : "Xác nhận"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </BottomSheetModal>
  )
}
